/*
 * Iter XX.42 — Academy Partners section (founder dashboard).
 * Read-only KPI tiles + per-partner rollup with cautious "estimate"
 * wording. Never says "You earned" — spec §Academy Dashboard.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

function fmtCents(cents, currency = "USD") {
  if (cents == null) return "—";
  const v = (cents / 100).toLocaleString(undefined, {
    style: "currency", currency, minimumFractionDigits: 0,
  });
  return v;
}

function fmtPct(x) {
  if (x == null) return "—";
  return `${Math.round((x || 0) * 100)}%`;
}

export function FounderPartnersSection() {
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/partners/overview");
        if (alive) { setOverview(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section data-testid="section-founder-partners" className="space-y-4">
      <div className="mb-1">
        <p className="label-eyebrow">academy partners · long-term program</p>
        <p className="text-xs text-neutral-500 mt-0.5">
          set-once attribution · net revenue only · no payouts yet · all values are estimates
        </p>
      </div>

      {err && <div className="surface-1 p-4 text-red-400 text-sm">{err}</div>}
      {!overview && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}

      {overview && (
        <>
          <div className="surface-1 p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4"
               data-testid="founder-partners-kpis">
            <Kpi label="partner academies"       v={overview.partner_academies} testid="fp-academies" />
            <Kpi label="players attributed"      v={overview.players_attributed} testid="fp-players" />
            <Kpi label="plus subscribers"        v={overview.plus_subscribers} testid="fp-plus" />
            <Kpi label="mrr (30d)"               v={fmtCents(overview.monthly_recurring_revenue_cents)} testid="fp-mrr" />
            <Kpi label="est. partner payout"     v={fmtCents(overview.estimated_partner_payout_cents)} testid="fp-payout" />
            <Kpi label="avg players / academy"   v={overview.avg_players_per_academy} testid="fp-avg" />
            <Kpi label="conversion"              v={fmtPct(overview.conversion_rate)} testid="fp-conv" />
            <Kpi label="top academy"
              v={overview.top_partner
                ? `${overview.top_partner.team_name} · ${overview.top_partner.players_attributed}p`
                : "—"}
              testid="fp-top" />
          </div>

          {(overview.per_partner || []).length > 0 && (
            <div className="surface-1 p-4" data-testid="founder-partners-list">
              <div className="label-eyebrow mb-3">per-academy rollup (estimates)</div>
              <div className="space-y-2">
                <div className="grid grid-cols-8 gap-2 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-800 pb-2">
                  <span className="col-span-2">academy</span>
                  <span>tier</span>
                  <span>players</span>
                  <span>plus</span>
                  <span>conv</span>
                  <span>est. monthly</span>
                  <span>est. payout</span>
                </div>
                {overview.per_partner.map((p) => (
                  <div key={p.team_id}
                       className="grid grid-cols-8 gap-2 text-xs items-center border-b border-neutral-900 pb-2"
                       data-testid={`fp-row-${p.team_id}`}>
                    <div className="col-span-2 text-neutral-200">
                      {p.team_name}
                      <div className="text-neutral-600 text-[10px] font-mono">/o/{p.slug}</div>
                    </div>
                    <div className="text-neutral-500">{p.tier}</div>
                    <div className="text-neutral-300 tabular-nums">{p.players_attributed}</div>
                    <div className="text-neutral-300 tabular-nums">{p.plus_members}</div>
                    <div className="text-neutral-500 tabular-nums">{fmtPct(p.conversion_rate)}</div>
                    <div className="text-neutral-300 tabular-nums">
                      {fmtCents(p.estimated_monthly_revenue_cents, p.currency)}
                    </div>
                    <div className="text-emerald-400 tabular-nums">
                      {fmtCents(p.estimated_next_payout_cents, p.currency)}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-neutral-500 mt-3">
                All figures are estimates. Payouts are not yet enabled. Net revenue excludes
                free passes, complimentary access, test accounts, refunds, chargebacks, and
                failed payments.
              </p>
            </div>
          )}

          {(overview.per_partner || []).length === 0 && (
            <div className="surface-1 p-6 text-neutral-500 text-sm" data-testid="founder-partners-empty">
              No enrolled partners yet. Use <span className="font-mono">POST /api/admin/partners/enroll</span> with
              a <span className="font-mono">team_id</span> to enrol the first academy.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Kpi({ label, v, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-xl font-light tabular-nums">{v ?? "—"}</div>
    </div>
  );
}

export default FounderPartnersSection;
